import {
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import {
  createCatalogItem,
  updateCatalogItem,
} from "../../api/catalog-items";
import CatalogItem, {
  MEASURE_UNIT_UNIT,
  newCatalogItem,
} from "../../models/catalog-item";
import { IVA_TYPE_10_PERCENT, IVA_TYPE_CHOICES } from "../../models/iva";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { emptyValueOnUndefinedOrNull } from "../../utils/fields";
import MeasureUnitSelect from "../measure-unit/MeasureUnitSelect";
import CurrencyField from "../currencies/CurrencyField";
import { errorAlert } from "../utils/messages";

interface CatalogItemFormProps {
  catalogItem?: CatalogItem;
  initialCurrency?: string;
  onCancel: () => void | Promise<void>;
  onSuccess: (_?: CatalogItem) => void | Promise<void>;
}

const CatalogItemForm: React.FC<CatalogItemFormProps> = ({
  catalogItem,
  initialCurrency,
  onCancel,
  onSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const isEditing = catalogItem !== undefined && catalogItem.id !== undefined;

  const [editingItem, setEditingItem] = useState<CatalogItem>(
    catalogItem ?? {
      ...newCatalogItem(taxPayer.id),
      priceCurrency: initialCurrency ?? PYG,
      // The tax rate every line of the panel starts at, so that picking the
      // entry while invoicing actually saves the step.
      ivaType: IVA_TYPE_10_PERCENT,
    },
  );
  const [submitting, setSubmitting] = useState(false);

  const onFieldChange = (field: keyof CatalogItem, value: unknown) => {
    setEditingItem((previous) => ({ ...previous, [field]: value }));
  };

  const onSubmit = async () => {
    if ((editingItem.description ?? "").trim() === "") {
      errorAlert("La descripción es obligatoria.");
      return;
    }

    setSubmitting(true);

    const payload: CatalogItem = {
      ...editingItem,
      taxPayerId: taxPayer.id,
      description: (editingItem.description ?? "").trim(),
      code: (editingItem.code ?? "").trim(),
      ivaType: editingItem.ivaType === "" ? null : editingItem.ivaType,
      unitPrice: editingItem.unitPrice ?? null,
      priceCurrency:
        editingItem.unitPrice == null
          ? null
          : editingItem.priceCurrency ?? PYG,
    };

    const status = isEditing
      ? await updateCatalogItem(catalogItem!.id!, payload)
      : await createCatalogItem(payload);

    if (status.status !== SUCCESS) {
      errorAlert(status.detail ?? "Ha ocurrido un error!!");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onSuccess(status.data);
  };

  return (
    <>
      <CRow>
        <CCol sm="12">
          <CFormGroup>
            <CLabel>
              <span className="text-danger">*</span> Descripción
            </CLabel>
            <CTextarea
              placeholder="Cómo se llama el producto o servicio"
              value={emptyValueOnUndefinedOrNull(editingItem.description)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFieldChange("description", e.target.value)
              }
              style={{ resize: "vertical", minHeight: "60px" }}
            ></CTextarea>
            <small className="form-text text-muted">
              Es lo que identifica al ítem y lo que se imprime en la factura.
            </small>
          </CFormGroup>
        </CCol>
      </CRow>
      <CRow>
        <CCol sm="4">
          <CFormGroup>
            <CLabel>Código</CLabel>
            <CInput
              type="text"
              placeholder="El código que uses vos"
              value={emptyValueOnUndefinedOrNull(editingItem.code)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFieldChange("code", e.target.value)
              }
            ></CInput>
            <small className="form-text text-muted">
              Opcional y libre. Podés repetirlo entre varios ítems si lo usás
              como categoría.
            </small>
          </CFormGroup>
        </CCol>
        <CCol sm="4">
          <CFormGroup>
            <CLabel>Tipo de IVA</CLabel>
            <CSelect
              value={emptyValueOnUndefinedOrNull(editingItem.ivaType)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFieldChange("ivaType", e.target.value)
              }
            >
              <option value={""}>Sin definir</option>
              {Array.from(IVA_TYPE_CHOICES.entries()).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
          </CFormGroup>
        </CCol>
        <CCol sm="4">
          <CFormGroup>
            <CLabel>Unidad de medida</CLabel>
            <MeasureUnitSelect
              value={editingItem.measureUnit ?? MEASURE_UNIT_UNIT}
              onChange={(code) => onFieldChange("measureUnit", code)}
            />
          </CFormGroup>
        </CCol>
      </CRow>
      <CRow>
        <CCol sm="6">
          <CFormGroup>
            <CLabel>Precio unitario</CLabel>
            <CurrencyField
              currency={editingItem.priceCurrency ?? PYG}
              placeholder="Opcional"
              value={editingItem.unitPrice ?? undefined}
              limit={1000000000000}
              onChange={(value) => onFieldChange("unitPrice", value ?? null)}
            />
            <small className="form-text text-muted">
              Se carga en la factura cuando la moneda coincide. Podés cambiarlo
              para cada venta.
            </small>
          </CFormGroup>
        </CCol>
        <CCol sm="6">
          <CFormGroup>
            <CLabel>Moneda del precio</CLabel>
            <CSelect
              value={editingItem.priceCurrency ?? PYG}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onFieldChange("priceCurrency", e.target.value)
              }
            >
              {Array.from(CURRENCY_CHOICES.entries()).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </CSelect>
          </CFormGroup>
        </CCol>
      </CRow>
      <CRow>
        <CCol sm="12" className="d-flex justify-content-end">
          <CButton
            color="secondary"
            className="mr-2"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancelar
          </CButton>
          <CButton color="primary" disabled={submitting} onClick={onSubmit}>
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <></>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CCol>
      </CRow>
    </>
  );
};

export default CatalogItemForm;
